import React, { useState, useRef, useCallback } from 'react';
import { useChatClient, useChatData } from '../context/ChatContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import AttachmentPreview from './AttachmentPreview';
import { encryptAndUploadAttachment } from '../services/attachmentService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip, faSpinner, faPaperPlane } from '@fortawesome/free-solid-svg-icons';

interface ComposerProps {
  conversationId: string;
}

const MAX_FILE_SIZES: Record<string, number> = {
  'image/': 10 * 1024 * 1024,
  'video/': 25 * 1024 * 1024,
  'audio/': 10 * 1024 * 1024,
  'application/pdf': 25 * 1024 * 1024,
  default: 5 * 1024 * 1024,
};

function getMaxSize(mimeType: string): number {
  for (const [prefix, size] of Object.entries(MAX_FILE_SIZES)) {
    if (prefix !== 'default' && mimeType.startsWith(prefix)) return size;
  }
  return MAX_FILE_SIZES.default;
}

const Composer: React.FC<ComposerProps> = ({ conversationId }) => {
  const { t } = useTranslation();
  const { chatAddress } = useChatClient();
  const { sendMessage } = useChatData();
  const [text, setText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = useCallback(async () => {
    if (isSending) return;

    const hasText = text.trim().length > 0;
    const hasFile = selectedFile !== null;

    if (!hasText && !hasFile) return;

    setIsSending(true);

    try {
      // Upload and send attachment if present
      if (hasFile && selectedFile) {
        setIsUploadingAttachment(true);
        try {
          const attachmentMeta = await encryptAndUploadAttachment(selectedFile, chatAddress || undefined);
          await sendMessage(conversationId, '', attachmentMeta);
          setSelectedFile(null);
        } catch (err: any) {
          toast.error('Failed to send attachment');
          console.error('Attachment upload error:', err);
          setIsSending(false);
          setIsUploadingAttachment(false);
          return;
        }
        setIsUploadingAttachment(false);
      }

      // Send text message
      if (hasText) {
        await sendMessage(conversationId, text.trim());
        setText('');
      }

    } catch (err: any) {
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  }, [text, selectedFile, conversationId, sendMessage, isSending]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = getMaxSize(file.type);
    if (file.size > maxSize) {
      toast.error(`File too large. Max size: ${(maxSize / (1024 * 1024)).toFixed(0)}MB`);
      return;
    }

    setSelectedFile(file);
    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  const canSend = (text.trim().length > 0 || selectedFile !== null) && !isSending;

  return (
    <div className="chat-composer">
      {/* Attachment preview */}
      {selectedFile && (
        <AttachmentPreview file={selectedFile} onRemove={handleRemoveFile} />
      )}

      <div className="chat-composer__input-row">
        {/* Attachment button */}
        <button
          className="chat-btn chat-btn--icon chat-composer__attach-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={isSending}
          title={t('chat.attachFile', 'Attach file')}
        >
          <FontAwesomeIcon icon={faPaperclip} />
        </button>

        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.json,.csv"
        />

        {/* Text input */}
        <textarea
          ref={textareaRef}
          className="chat-composer__textarea"
          placeholder={t('chat.typeMessage', 'Type a message...')}
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          rows={1}
          disabled={isSending}
        />

        {/* Send button */}
        <button
          className="chat-btn chat-btn--primary chat-composer__send-btn"
          onClick={handleSend}
          disabled={!canSend}
          title={t('chat.send', 'Send')}
        >
          {isSending ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <FontAwesomeIcon icon={faPaperPlane} />
          )}
        </button>
      </div>

      {isUploadingAttachment && (
        <div className="chat-composer__uploading">
          <FontAwesomeIcon icon={faSpinner} spin /> {t('chat.uploading', 'Encrypting & uploading...')}
        </div>
      )}
    </div>
  );
};

export default Composer;
